import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../App.css'; 
import './PdfDetails.css'; 

const PdfDetails = () => {
  const { id } = useParams();
  const [pdf, setPdf] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false); 
  const [isDownloadingOriginal, setIsDownloadingOriginal] = useState(false); 
  const [tableData, setTableData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Helper function to check if the token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    const payload = JSON.parse(atob(token.split('.')[1])); // Decode token payload
    const exp = payload.exp; // Expiration time
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    return exp < currentTime; // Return true if token expired
  };

  // Function to refresh the token
  const refreshAuthToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        return true;
      } else {
        console.error('Failed to refresh token');
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  };

  // Function to handle PDF download
  const handleDownloadPDF = async () => {
    setIsDownloadingOriginal(true);
    let token = localStorage.getItem('token');

    if (!token || isTokenExpired(token)) {
      const refreshed = await refreshAuthToken();
      if (!refreshed) {
        navigate('/login');
        setIsDownloadingOriginal(false);
        return;
      }
      token = localStorage.getItem('token'); // Update with refreshed token
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/download-pdf/${pdf.name}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${pdf.name}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(`Failed to download ${pdf.name || 'file'}. Please try again later.`);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(`Error downloading ${pdf.name || 'file'}`);
    } finally {
      setIsDownloadingOriginal(false);
    }
  };

  // Function to generate CSV data
  const generateCSVData = () => {
    if (!tableData || tableData.data.length === 0) return '';
    const { data, headers } = tableData;

    // Build CSV lines:
    const csvRows = [
      headers.map(formatHeader).join(','), // CSV header row
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            // Quote the values to ensure commas don't break the CSV structure
            return Array.isArray(value)
              ? `"${value.join(', ')}"`
              : value !== undefined && value !== null
              ? `"${value}"`
              : '';
          })
          .join(',')
      ),
    ];

    return csvRows.join('\n');
  };

  // Function to handle CSV download
  const handleDownloadCSV = () => {
    setIsDownloading(true);

    // Generate your CSV data
    let csvData = generateCSVData();
    csvData = '\uFEFF' + csvData;

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    // Set file name
    const fileName = pdf?.name ? pdf.name.replace(/\.pdf$/i, '') : 'data';
    link.setAttribute('download', `${fileName}.csv`);

    link.href = url;
    document.body.appendChild(link);

    link.addEventListener('click', () => {
      setTimeout(() => {
        setIsDownloading(false);
        window.URL.revokeObjectURL(url);
      }, 500);
    });

    link.click();
    document.body.removeChild(link);
  };

  // Fetch PDF data on mount
  useEffect(() => {
    const fetchPdf = async () => {
      setIsLoading(true);
      let token = localStorage.getItem('token');

      if (!token || isTokenExpired(token)) {
        const refreshed = await refreshAuthToken();
        if (!refreshed) {
          navigate('/login');
          setIsLoading(false);
          return;
        }
        token = localStorage.getItem('token');
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_FQDN}/api/pdf/${id}`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          setPdf(data.pdf);

          if (data.pdf && Array.isArray(data.pdf.result)) {
            const { data: tableDataArray, headers } = processPdfResult(data.pdf.result);
            setTableData({ data: tableDataArray, headers });
          } else {
            setTableData({ data: [], headers: [] });
          }
        } else if (response.status === 404) {
          setPdf({ name: '', result: [] });
          setTableData({ data: [], headers: [] });
        } else {
          alert('An unexpected error occurred. Please try again later.');
        }
      } catch (error) {
        console.error('Error fetching PDF details:', error);
        alert('Failed to load PDF details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPdf();
  }, [id, navigate]);

  return (
    <div style={{ maxWidth: "100%", margin: "0 auto", overflowX: "hidden" }}>
      <h2>PDF Details</h2>
      {isLoading ? (
        <p>Loading PDF details...</p>
      ) : pdf ? (
        <>
          {/* Row with File Name and Buttons */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            <p>
              File Name: <strong>{pdf.name}</strong>
            </p>
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              style={{ width: "300px", marginBottom: "10px" }}
            >
              {isDownloadingOriginal ? 'Downloading...' : 'Download Original Uploaded PDF'}
            </button>
            <button
              onClick={handleDownloadCSV}
              disabled={isDownloading}
              style={{ width: "300px", marginBottom: "10px" }}
            >
              {isDownloading ? 'Downloading...' : 'Download CSV with Results'}
            </button>
          </div>

          {/* Row with Table */}
          <h4 style={{ textAlign: 'left', marginBottom: '-10px'}}>Identified Claims:</h4>
          <div style={{ overflowX: "auto" }}>
            <DynamicTable tableData={tableData} />
          </div>
        </>
      ) : (
        <p>No PDF details available.</p>
      )}
    </div>
  );
};

const desiredOrder = [
  'agreement number',
  'agreement number (alternates)',
  'lender',
  'lender (alternates)',
  'intermediary',
  'intermediary (alternates)',
  'client',
  'client (alternates)',
  'vrm',
  'vrm (alternates)',
  'vin',
  'vin (alternates)',
  'cost of goods',
  'cost of goods (alternates)',
  'advanced payment',
  'advanced payment (alternates)',
  'deposit',
  'deposit (alternates)',
  'interest charged',
  'interest charged (alternates)',
  'final payment',
  'final payment (alternates)',
  'part exchange',
  'part exchange (alternates)',
  'loan amount',
  'loan amount (alternates)',
  'interest rate',
  'interest rate (alternates)',
  'apr',
  'apr (alternates)',
  'start date',
  'start date (alternates)',
  'end date',
  'end date (alternates)',
  'term period',
  'term period (alternates)',
  'number of monthly payments',
  'number of monthly payments (alternates)',
  'monthly payment',
  'monthly payment (alternates)',
  'amount payable',
  'amount payable (alternates)',
  'commission',
];

// Helper to normalize strings for case-insensitive matching
const normalizeKey = (key) => (key || '').toLowerCase();

const reorderHeaders = (actualHeaders) => {
  const finalHeaders = [];

  desiredOrder.forEach((desired) => {
    const match = actualHeaders.find((h) => normalizeKey(h) === normalizeKey(desired));
    if (match) {
      finalHeaders.push(match);
    }
  });

  const leftover = actualHeaders.filter(
    (h) => !finalHeaders.includes(h)
  );

  finalHeaders.push(...leftover);

  return finalHeaders;
};

const DynamicTable = ({ tableData }) => {
  if (!tableData || tableData.data.length === 0) {
    return <p>No data available.</p>;
  }

  const { data, headers } = tableData;

  const orderedHeaders = reorderHeaders(headers);

  return (
    <table className="dynamic-table">
      <thead>
        <tr>
          {orderedHeaders.map((header) => (
            <th key={header} style={{ minWidth: '200px' }}>
              {formatHeader(header)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, index) => (
          <tr key={index}>
            {orderedHeaders.map((header) => (
              <td key={header} style={{ minWidth: '200px', verticalAlign: 'middle' }}>
                <div style={{ maxHeight: '60px', overflowY: 'auto' }}>
                  {Array.isArray(row[header])
                    ? row[header].join(', ')
                    : row[header] || ''}
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const formatHeader = (header) => {
  const specialCases = {
    vrm: 'VRM',
    vin: 'VIN',
    id: 'ID',
    accountnumber: 'Acct No.',
    apr: 'APR',
  };

  for (const key in specialCases) {
    const regex = new RegExp(`^(${key})(.*)$`, 'i');
    const match = header.match(regex);
    if (match) {
      const specialPart = specialCases[key];
      const rest = match[2];
      const formattedRest = rest
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
      return `${specialPart}${formattedRest}`.trim();
    }
  }

  return header
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

const processPdfResult = (result) => {
  if (!Array.isArray(result)) {
    return { data: [], headers: [] };
  }

  const allKeys = new Set();
  result.forEach((item) => {
    Object.keys(item).forEach((key) => allKeys.add(key));
  });

  return { data: result, headers: Array.from(allKeys) };
};

export default PdfDetails;
